import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import PrescriptionBody from './prescriptionBody';
import "./prescription-print.scss"
import PrescriptionHeader from './prescriptionHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faPrint } from '@fortawesome/free-solid-svg-icons';
import ReactToPrint from 'react-to-print';
import PrescriptionFooter from './prescriptionFooter';
import { dateFormat } from '../../../utils/common-funtions/common';
import { get, put } from '../../../utils/fetchAPI';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedRole, setUserRoles } from '../../../store/slices/authSlice';
import { Toast } from '../../../utils/SwalUti';

// Mirrors entries in the Prescription settings tab, same keys and same labels, so
// the toolbar and the settings page stay in step.
const PRINT_TOGGLES = [
    ['signatureWithImage', 'Signature field with image'],
    ['signatureWithoutImage', 'Signature field without image'],
    ['showDiscountNote', 'Show discount note'],
];

const PrescriptionPrint = ({ patientId, visitId, setShowPrint, visitDetails, medicineAndAdviceOnly = false }) => {
    const ref = useRef()
    const reactToPrintRef = useRef();
    const [patientDetails, setPatientDetails] = useState()
    const [details, setDetails] = useState()
    const [isHeader, setIsHeader] = useState(true)
    const [isFooter, setIsFooter] = useState(true)
    const [doctorDetails, setDoctorDetails] = useState([])
    const [savingToggleKey, setSavingToggleKey] = useState(null)
    const { user, userRoles, selectedRole } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const doctorIdForSignature = useMemo(
        () =>
            visitDetails?.doctor ||
            (user?.user?.type?.toLowerCase() === 'doctor' ? user?.user?._id : null),
        [visitDetails?.doctor, user?.user?.type, user?.user?._id]
    )

    const viewingOthersVisit = Boolean(
        visitDetails?.doctor && String(visitDetails.doctor) !== String(user?.user?._id)
    )

    const [doctorSignature, setDoctorSignature] = useState(undefined)

    useEffect(() => {
        if (!doctorIdForSignature) {
            setDoctorSignature(undefined)
            return
        }
        let cancelled = false
        get(`/doctor/${doctorIdForSignature}/signature`)
            .then((res) => {
                if (!cancelled && res?.status === 200) {
                    setDoctorSignature(res.data?.signature ?? null)
                }
            })
            .catch(() => {
                if (!cancelled) setDoctorSignature(null)
            })
        return () => {
            cancelled = true
        }
    }, [doctorIdForSignature])

    let bodySignature
    let bodySignatureControlled = false
    if (!doctorIdForSignature) {
        bodySignature = user?.user?.details?.files?.signature
    } else if (viewingOthersVisit) {
        bodySignatureControlled = true
        bodySignature = doctorSignature === undefined ? null : doctorSignature
    } else {
        bodySignature =
            doctorSignature !== undefined ? doctorSignature : user?.user?.details?.files?.signature
    }

    const printVisitId = visitId || visitDetails?._id

    useEffect(() => {
        if (patientId)
            getPatientDetails(patientId)
    }, [patientId]);

    useEffect(() => {
        // Doctor logged in: use their own selected-role config (previous behavior).
        // Assistant / non-doctor: resolve config from the visit's doctor + location.
        const isDoctor = user?.user?.type?.toLowerCase() === 'doctor'
        if (isDoctor && selectedRole) {
            setDoctorDetails(selectedRole)
            setIsHeader(selectedRole?.config?.prescriptionHeaderInPrint)
            setIsFooter(selectedRole?.config?.prescriptionFooterInPrint)
        } else if (printVisitId) {
            getVisitPrescriptionConfig(printVisitId)
        }
    }, [printVisitId, selectedRole, user?.user?.type]);



    async function getPatientDetails(patientId) {
        await get(`/patient/${patientId}`)
            .then((res) => {
                if (res?.status === 200) {
                    setPatientDetails(res.data)
                }
            })
            .catch((error) => {
                console.log(error);
            });
    }

    async function getVisitPrescriptionConfig(visitId) {
        await get(`/visit/${visitId}/prescription-config`)
            .then((res) => {
                if (res?.status === 200) {
                    const config = res.data?.config || {}
                    setDoctorDetails({ config })
                    setIsHeader(config?.prescriptionHeaderInPrint)
                    setIsFooter(config?.prescriptionFooterInPrint)
                }
            })
            .catch((error) => {
                console.log(error);
            });
    }

    useMemo(() => {
        if (visitDetails)
            setDetails(visitDetails)
    }, [visitDetails])

    // These toggles persist to the logged-in doctor's own role config, so they are
    // only offered when that config is what's driving the preview. An assistant
    // printing someone else's visit is reading that doctor's config via
    // /visit/:id/prescription-config and has no role of their own to save against.
    const canEditPrintSettings = Boolean(
        user?.user?.type?.toLowerCase() === 'doctor' && selectedRole?._id
    )

    const togglePrintSetting = async (key) => {
        if (!canEditPrintSettings || savingToggleKey) return

        const nextValue = !doctorDetails?.config?.[key]
        const previousConfig = doctorDetails?.config

        setSavingToggleKey(key)
        // Optimistic, so the preview redraws before the round-trip finishes.
        setDoctorDetails((prev) => ({ ...prev, config: { ...prev?.config, [key]: nextValue } }))

        await put(`user/role/update/${selectedRole._id}`, { [`config.${key}`]: nextValue })
            .then((res) => {
                if (res?.status === 200) {
                    // Same store update the settings page does, so the new value is the
                    // default for the next print and the settings tab shows it too.
                    const updatedRole = {
                        ...selectedRole,
                        config: res?.data?.config || { ...selectedRole?.config, [key]: nextValue },
                    }
                    const roles = [...(userRoles || [])]
                    const index = roles.findIndex((item) => item?._id === updatedRole?._id)
                    if (index > -1) roles[index] = updatedRole
                    dispatch(setSelectedRole(updatedRole))
                    dispatch(setUserRoles(roles))
                } else {
                    setDoctorDetails((prev) => ({ ...prev, config: previousConfig }))
                }
            })
            .catch(() => {
                setDoctorDetails((prev) => ({ ...prev, config: previousConfig }))
                Toast.fire({ icon: 'error', title: 'Could not save print setting' })
            })
            .finally(() => setSavingToggleKey(null))
    }


    const handleKeyDown = useCallback((event) => {
        // CTRL + P
        if ((event.shiftKey && event.ctrlKey) && (event.key === "p" || event.key === "P")) {
            event.preventDefault();
            if (reactToPrintRef?.current) {
                reactToPrintRef.current.handlePrint();
            }
        }
        // ESC to close
        if (event.key === "Escape") {
            setShowPrint(false);
        }
    }, [setShowPrint]);

    // ADD EVENT LISTENER
    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);


    return (
        <>
            <div className="mt-0 pt-0 d-flex justify-content-between align-items-center p-2">
                <div className='d-flex'>
                    <ReactToPrint
                        ref={reactToPrintRef}
                        trigger={() => <p className=" mt-0 pt-0 mb-0 mr-2 d-inline-block px-2 rounded" style={{ cursor: "pointer", background: "#13AC81", color: "white" }}>Print <FontAwesomeIcon className="mt-0 pt-0" icon={faPrint} /></p>}
                        content={() => ref.current}
                        onBeforePrint={() => document.title = dateFormat(patientDetails?.updatedAt?.slice(0, 10)) + "-" + patientDetails?.name + "-" + patientDetails?.patientId
                        }
                        onAfterPrint={() => {
                            document.title = "Shafa-Emr"
                            setShowPrint(false)
                        }}
                    />
                </div>
                <div className='d-flex align-items-center'>
                    {canEditPrintSettings &&
                        <div className='print-setting-toggles d-flex align-items-center mr-3'>
                            {PRINT_TOGGLES.map(([key, label]) => (
                                <label key={key} className='print-setting-toggle mb-0'>
                                    <input
                                        type='checkbox'
                                        checked={Boolean(doctorDetails?.config?.[key])}
                                        disabled={Boolean(savingToggleKey)}
                                        onChange={() => togglePrintSetting(key)}
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    }
                    <p onClick={() => { setShowPrint(false) }} style={{ cursor: "pointer", color: "#098F6A" }} className="mt-0 pt-0 mb-0 print-close-button">Close <FontAwesomeIcon className="mt-0 pt-0" icon={faCircleXmark} /></p>
                </div>
            </div>
            <div style={{ minHeight: "1700px" }}>
                {!doctorDetails?.config?.prescriptionHeaderInPrint && doctorDetails?.config?.prescriptionHeaderHeight && <div style={{ height: doctorDetails?.config?.prescriptionHeaderHeight + "px" }}></div>}
                <div ref={ref} className='full-prescription' style={{ minheight: "90vh", margin: doctorDetails?.config?.prescriptionMargin || selectedRole?.config?.prescriptionMargin || "5px px 5px 5px" }}>
                    <style>
                        {`
                       @page { 
                       margin-top: ${!doctorDetails?.config?.prescriptionHeaderInPrint && doctorDetails?.config?.prescriptionHeaderHeight || 0}px;
                       margin-bottom: ${!doctorDetails?.config?.prescriptionFooterInPrint ? "180px" : "0px"};
                      }
                    `}
                    </style>
                    <PrescriptionHeader medicineAndAdviceOnly={medicineAndAdviceOnly} isHeader={isHeader} info={patientDetails} drDetails={doctorDetails || []}></PrescriptionHeader>
                    <PrescriptionBody
                        drDetails={doctorDetails || []}
                        patientDetails={patientDetails}
                        info={details}
                        signature={bodySignature}
                        signatureControlled={bodySignatureControlled}
                        medicineAndAdviceOnly={medicineAndAdviceOnly}
                    ></PrescriptionBody>
                    <PrescriptionFooter medicineAndAdviceOnly={medicineAndAdviceOnly} isFooter={isFooter} drDetails={doctorDetails || []}></PrescriptionFooter>
                </div>
            </div>
        </>
    )
}

export default PrescriptionPrint



// import React, { useEffect, useMemo, useRef, useState } from 'react'
// import PrescriptionBody from './prescriptionBody';
// import "./prescription-print.scss"
// import PrescriptionHeader from './prescriptionHeader';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faCircleXmark, faPrint } from '@fortawesome/free-solid-svg-icons';
// import ReactToPrint from 'react-to-print';
// import PrescriptionFooter from './prescriptionFooter';
// import { dateFormat } from '../../../utils/common-funtions/common';
// import { get } from '../../../utils/fetchAPI';
// import { useSelector } from 'react-redux';

// const PrescriptionPrint = ({ patientId, visitId, setShowPrint, visitDetails }) => {
//     const ref = useRef()
//     const [patientDetails, setPatientDetails] = useState()
//     const [details, setDetails] = useState()
//     const [isHeader, setIsHeader] = useState(true)
//     const [isFooter, setIsFooter] = useState(true)
//     const [doctorDetails, setDoctorDetails] = useState([])
//     const { user, userRoles, selectedRole } = useSelector((state) => state.auth);
//     const reactToPrintRef = useRef();

//     useEffect(() => {
//         if (patientId)
//             getPatientDetails(patientId)
//         if (user?.user?.type?.toLowerCase() === 'doctor' && selectedRole) {
//             setDoctorDetails(selectedRole)
//             setIsHeader(selectedRole?.config?.prescriptionHeaderInPrint)
//             setIsFooter(selectedRole?.config?.prescriptionFooterInPrint)
//         }
//         else if (visitDetails?.doctor) {
//             getDoctorDetails(visitDetails?.doctor)
//         }
//     }, [selectedRole]);



//     async function getPatientDetails(patientId) {
//         await get(`/patient/${patientId}`)
//             .then((res) => {
//                 if (res?.status === 200) {
//                     setPatientDetails(res.data)
//                 }
//             })
//             .catch((error) => {
//                 console.log(error);
//             });
//     }

//     async function getDoctorDetails(doctorId) {
//         await get(`/user/${doctorId}`)
//             .then((res) => {
//                 if (res?.status === 200) {
//                     setDoctorDetails(res.data)
//                     setIsHeader(res.data.details?.config?.prescriptionHeaderInPrint)
//                     setIsFooter(res.data.details?.config?.prescriptionFooterInPrint)
//                 }
//             })
//             .catch((error) => {
//                 console.log(error);
//             });
//     }



//     const handleKeyDown = (event) => {
//         if (event.ctrlKey && event.key === "p") {
//             event.preventDefault(); // Prevent default browser print behavior
//             if (reactToPrintRef?.current) {
//                 reactToPrintRef?.current?.handlePrint(); // Trigger print programmatically
//             }
//         }
//         if (event.key === "Escape") {
//             setShowPrint(false)
//          }
//     };

//     useEffect(() => {
//         document.addEventListener("keydown", handleKeyDown);
//         return () => {
//             document.removeEventListener("keydown", handleKeyDown);
//         };
//     }, []);

//     useMemo(() => {
//         if (visitDetails)
//             setDetails(visitDetails)
//     }, [visitDetails])

//     return (
//         <>
//             <div className="mt-0 pt-0 d-flex justify-content-between p-2">
//                 <div className='d-flex' id="react-to-print-1">
//                     <ReactToPrint
//                         ref={reactToPrintRef}
//                         trigger={() => <p className=" mt-0 pt-0 mr-2 d-inline-block px-2 rounded" style={{ cursor: "pointer", background: "#13AC81", color: "white" }}>Print {"(ctrl+p)"}  <FontAwesomeIcon className="mt-0 pt-0" icon={faPrint} /></p>}
//                         content={() => ref.current}
//                         onBeforePrint={() => document.title = dateFormat(patientDetails?.updatedAt?.slice(0, 10)) + "-" + patientDetails?.name + "-" + patientDetails?.patientId
//                         }
//                         onAfterPrint={() => document.title = "Shafa-Emr"}
//                     />
//                 </div>
//                 <p onClick={() => { setShowPrint(false) }} style={{ cursor: "pointer" }} className="mt-0 pt-0"><FontAwesomeIcon className="mt-0 pt-0" icon={faCircleXmark} /></p>
//             </div>
//             <div style={{ height: "1700px" }}>
//                 <div ref={ref} className='full-prescription' style={{ height: "90vh", margin: selectedRole?.config?.prescriptionMargin || "5px px 5px 5px" }}>
//                     <PrescriptionHeader isHeader={isHeader} info={patientDetails} drDetails={doctorDetails || []}></PrescriptionHeader>
//                     <PrescriptionBody drDetails={doctorDetails || []} patientDetails={patientDetails} info={details}></PrescriptionBody>
//                     <PrescriptionFooter isFooter={isFooter} drDetails={doctorDetails || []}></PrescriptionFooter>
//                 </div>
//             </div>
//         </>
//     )
// }

// export default PrescriptionPrint
